/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, type Type, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type ActionAbstract, type Command, type HttpResponse,
    type JobResponse, type OptionsParams, type OptionsResult,
    type Rest, DMValidators, ExtendService, FormControl, FormGroup,
    FormSlideWindowAbstract, SelectComponent, ToastService, UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { FileSystemModeE } from 'app/common/constants';
import { SmartGuiService, TaskService, ToolService } from 'app/common/services';
import { type Dtree, type FileSystem, type NFSClient, type NFSShare, type Omtask } from 'app/common/types/mo';
import { ProductConfig } from 'app/config';
import { cloneDeep } from 'lodash';
import { type Subscription, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, flatMap, map, mergeMap, tap } from 'rxjs/operators';

import { DtreeService } from '../../../dtree/dtree.service';
import { CreateFileSystemComponent, FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { FsSmartGuiInfoComponent } from '../../../file-system/fs-smartgui-info/fs-smartgui-info.component';
import { type AddNfsClientParams, type CreateNfsParams, type NFSShareVm, type SmartguiNfsshareReceived } from '../../share';
import { CharEncodingE, GlobalNamespaceE, OffsetE } from '../nfs-share.enum';
import { NfsShareStrService } from '../nfs-share-str.service';
import { ClientNfsShareService } from './../client-nfs-share/client-nfs-share.service';
import { NfsShareService } from './../nfs-share.service';

@Component({
    selector: 'app-common-create-nfs-share',
    templateUrl: './create-nfs-share.component.html',
    styleUrls: ['./create-nfs-share.component.scss'],
})
export class CreateNfsShareComponent extends FormSlideWindowAbstract implements OnInit {
    i18n = this.str.i18n;
    @Input() fileSystem: FileSystem = {};
    @Input() dtree: Dtree = {};
    @Input() vstoreId: string;
    @Input() vstoreName: string;
    // 模板创建的共享
    @Input() createAsShare: NFSShare;
    // 是否支持智能GUI，默认支持，当前copyfrom 和createas不支持
    @Input() supportSmartGui = true;

    /**
     * copy from的方式，有new 和copyfrom两种。如果是copy from过来的，必填字段
     */
    @Input() copyFromStyle: 'new' | 'copyFrom';
    // copy from 过来的title是要替换的
    @Input() title: string = this.i18n.title.createTitle;
    /**
     * copy from传过来的share属性
     */
    @Input() copyFromShare: NFSShare;
    /**
     * copy from传过来的clients列表,new 和 copyfrom模式下都有，第一次copyfrom过来的是undefined
     */
    @Input() copyFromPermissions: NFSClient[];

    @Input() entry: FileSystemEntryE;

    @ViewChild('fsSelect') fsSelect: SelectComponent;

    UNPLACEHOLDER = UNPLACEHOLDER;
    ProductConfig = ProductConfig;

    closeArgs = {
        refresh: false,
        data: null,
    };

    /**
     * 侧划框忙等
     */
    pathLoading = false;

    loading = false;

    vm: FormGroup;
    errorMessage: any;

    get vmValue(): NFSShareVm {
        return this.vm ? this.vm.value : null;
    }

    fileSystemRest: Rest;
    dtreeRest: Rest;

    isRootDirectory = false;
    charEncodingOpts: SelectOption[] = this.str.charEncodingOpts;
    advanced = false;
    showAdvance = true;

    // 页面要展示的share_path
    get share_path(): string {
        // 如果是copyfrom过来的，sharepath 就固定从copyfrom share中获取，不会变动。
        // 这么写是为了防止share_path被其他逻辑给赋值了，避免了路径为空的时候显示 --
        return this.copyFromStyle ? this.copyFromShare.SHAREPATH : this._share_path;
    }
    set share_path(value: string) {
        this._share_path = value;
    }
    _share_path = '';
    share_name = '';
    // 客户端列表, 默认用copyFrom的
    clientList: NFSClient[] = [];
    // 一次查询clients的分页数量，只有copy from 的第一次打开侧划框才有用
    maxClientsRange = 100;
    // 查询全量Clients的忙等
    allClientLoading = false;
    // 是否允许更改文件系统
    canFileSystemChange = true;
    // 是否允许更改dtree
    canDtreeChange = true;
    //  是否显示dtree
    showDtree = true;
    /** 是否有Dtree读权限，没有则不能选择Dtree */
    hasDtreePermission = this.permissionService.hasPermission('dtree_R');

    selectedFilesystem: FileSystem;
    /**
     * 当前共享是否基于审计日志文件系统
     */
    baseOnAuditLogFs: boolean;
    // 创建文件系统按钮灰化标识
    createFsDisabled = false;

    // 从接口获取的NFS智能推荐数据
    nfsSmartguiData: SmartguiNfsshareReceived = {};

    createFsTaskId: string;

    createFs$: Subscription;

    fsOptionsFactory: (params: OptionsParams) => Observable<OptionsResult>;

    constructor(
        injector: Injector,
        public str: NfsShareStrService,
        private NFSService: NfsShareService,
        private clientService: ClientNfsShareService,
        private toolService: ToolService,
        private dtreeService: DtreeService,
        private smartguiService: SmartGuiService,
        private extend: ExtendService,
        private utilService: UtilService,
        private toastService: ToastService,
        private taskService: TaskService,

    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (!this.checkDepPermission(['file_system_R'])) {
            return;
        }
        // 如果是new，并且传入了客户端列表，就直接赋值
        if (this.copyFromStyle === 'new' && this.copyFromPermissions) {
            // 深度拷贝一把，不然修改客户端之后，没有点确定，客户端的数据依然被更改了。
            this.clientList = cloneDeep(this.copyFromPermissions);
        } else if (this.copyFromStyle === 'copyFrom' && !this.copyFromPermissions) {
            // copyFrom模式下copyFromPermissions如果是undefined，那就说明是第一次点开，才需要去查询全量的clients
            this.getAllClients(this.copyFromShare);
        } else if (this.copyFromStyle === 'copyFrom' && this.copyFromPermissions) {
            // copyFrom模式下copyFromPermissions如果是数组，那就说明是已经查询过全量数据了，就不需要查询了,直接赋值
            this.clientList = cloneDeep(this.copyFromPermissions);
        }

        // 如果是模板创建，也需要查询所有的客户端数据
        if (this.createAsShare) {
            this.getAllClients(this.createAsShare);
        }

        this.entryHandler();
        this.fsOptionsFactory = this.createNfsOptionsFactory();

        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;

        // 如果不是从copyFrom和createas打开的则获取CIFS智能推荐数据, 从copyFrom打开copyFromStyle一定有值
        if (this.supportSmartGui && !this.copyFromStyle) {
            this.getNfsSmartguiData();
        }
    }

    createNfsOptionsFactory(): ({ limit, offset, keyword }: OptionsParams) => Observable<{
        finished: boolean;
        options: any;
    }> {
        return ({ limit, offset, keyword }: OptionsParams) => {
            const filters = [];
            // 是否添加根目录文件系统，首次请求文件系统数据或者搜索"/"时增加
            const isAddRootFs = (offset === OffsetE.NONE && keyword === '') || keyword === GlobalNamespaceE.NAME;
            if (keyword) {
                filters.push({
                    field: 'NAME',
                    value: keyword,
                    matchMode: ':',
                });
            }

            const range = `[${offset}-${offset + limit}]`;
            const params = {
                vstoreId: this.vstoreId,
                range,
            };

            return forkJoin({
                count: this.NFSService.getBatchCountFs(filters, { vstoreId: this.vstoreId }),
                fileSystems: this.NFSService.getBatchFs(filters, params),
            }).pipe(
                map((res) => {
                    const fsOptions = res.fileSystems.data.map((vstore) => ({
                        label: vstore.NAME,
                        value: vstore.ID,
                        data: vstore,
                    }));

                    if (isAddRootFs) {
                        fsOptions.unshift({
                            label: GlobalNamespaceE.NAME,
                            value: GlobalNamespaceE.ID,
                            data: {
                                ID: GlobalNamespaceE.ID,
                                NAME: GlobalNamespaceE.NAME,
                                vstoreId: this.vstoreId,
                            },
                        });
                    }

                    const count = res.count.data.COUNT;

                    return {
                        finished: Number(count) <= (offset + limit),
                        options: fsOptions,
                    };
                }),
            );
        };
    }

    getAllClients(share): void {
        this.allClientLoading = true;
        this.clientService.getCount(share.ID, share.vstoreId)
            .pipe(
                flatMap(({ data }) => {
                    const count = Number(data.COUNT);
                    if (count === 0) {
                        return [];
                    }
                    // 计算重复查询次数
                    const times = Math.floor(count / this.maxClientsRange) + (count % this.maxClientsRange === 0 ? 0 : 1);
                    const cmds: Observable<HttpResponse>[] = [];
                    // 循环构造所有的查询接口
                    for (let i = 0; i < times; i++) {
                        cmds.push(
                            this.clientService.getBatch(share.ID, share.vstoreId,
                                [i * this.maxClientsRange, (i + 1) * this.maxClientsRange]),
                        );
                    }
                    return this.extend.forkOrder(cmds);
                }),
                tap((results) => {
                    const datas: NFSClient[] = [];
                    results.forEach((result) => {
                        if (Array.isArray(result.data)) {
                            datas.push(...result.data);
                        }
                    });
                    this.clientList = datas;
                }),
                finalize(() => {
                    this.allClientLoading = false;
                }),
            ).subscribe();
    }

    /**
     * 获取NFS智能推荐数据
     */
    getNfsSmartguiData(): void {
        if (!this.smartguiService.switch) {
            return;
        }

        // 添加忙等效果
        this.showLoading();
        this.NFSService.getSmartguiNFS(this.vstoreId, { requestTimeoutTip: this.i18n.error.smartRecommendDataTimeoutMsg })
            .pipe(
                flatMap(({ data }) => {
                    this.nfsSmartguiData = {
                        ...this.nfsSmartguiData,
                        ...data,
                    };
                    if (this.nfsSmartguiData.ENABLESHOWSNAPSHOT) {
                        this.vm.get('ENABLESHOWSNAPSHOT').setValue(this.nfsSmartguiData.ENABLESHOWSNAPSHOT === 'true');
                    }
                    if (this.nfsSmartguiData?.filesystem) {
                        const queryParams = {
                            name: this.nfsSmartguiData.filesystem,
                            vstoreId: this.vstoreId,
                        };
                        return this.NFSService.getFSOnName(queryParams);
                    } else {
                        return of(null);
                    }
                }),
                finalize(() => this.hideLoading()),
            )
            .subscribe((resp: HttpResponse | null) => {
                if (resp?.data.length && this.fileSystem && !this.fileSystem.NAME) {
                    const { data: [item] } = resp;
                    this.fsSelect.setFixedOption({
                        value: item && item.ID,
                        label: item && item.NAME,
                        data: item,
                    });
                }
            });
    }

    /**
     * 入口的判断和处理
     */
    entryHandler(): void {
        // 如果传入了文件系统，就证明是从文件系统或dtree入口进来的，此时不允许修改文件系统和dtree
        if (this.fileSystem && this.fileSystem.NAME) {
            this.share_path = `/${this.fileSystem.NAME}/`;
            this.canFileSystemChange = false;
            this.selectedFilesystem = this.fileSystem;
            this.baseOnAuditLogFs = this.selectedFilesystem.isAuditLogFs;

            if (this.dtree && this.dtree.NAME) {
                this.pathLoading = true;
                this.canDtreeChange = false;
                this.NFSService.getDtree(this.dtree.ID, this.vstoreId).pipe(
                    finalize(() => {
                        this.pathLoading = false;
                    }),
                ).subscribe(({ data }: { data: Dtree }) => {
                    this.share_path = this.dtreeService.getDtreeFullPath(this.fileSystem, data);
                });
            } else {
                // 未传入有效Dtree说明是从文件系统详情进入，直接不显示Dtree
                this.showDtree = false;
            }

            // 审计日志文件系统、哈希文件系统和内部文件系统要限制共享权限
            if (this.fileSystem.isAuditLogFs || this.fileSystem.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
                this.fileSystem.SUBTYPE === FileSystemModeE.HASH) {
                this.entry = FileSystemEntryE.AuditLog;
            }
            // 如果是模板创建共享
        } else if (this.createAsShare && this.createAsShare.FSID) {
            this.getAuditLogFsInfo(this.createAsShare.FSID);
        }
    }

    /**
     * 复写合法性校验，增加依赖权限控制
     */
    validate(): Observable<boolean> {
        return this.vm.validateOb()
            .pipe(
                mergeMap((validate: boolean) => {
                    if (!validate) {
                        return of(false);
                    }
                    // 权限校验不通过会触发error
                    const checkPermission: Observable<boolean> = this.permissionService.checkDepPermission(['task_W'])
                        .pipe(map(() => true));
                    // 选了客户端会走omtask创共享，需要检查task写权限
                    return this.clientList.length ? checkPermission : of(true);
                }),
                catchError(() =>
                    // 校验不通过
                    of(false),
                ),
            );
    }

    /**
     * 复写modify方法，传入文件系统时，需要直接true 让buldcmd执行
     */
    isModified(): boolean {
        if (this.fileSystem && this.fileSystem.NAME) {
            return true;
        }
        return this.vm.valueChanged;
    }

    initVm(): void {
        // 初始化默认数据
        const defaultShare = {
            NAME: this.copyFromStyle ? this.copyFromShare.NAME : '',
            DESCRIPTION: this.copyFromStyle ? this.copyFromShare.DESCRIPTION : '',
            FS: this.selectedFilesystem,
            dtree: null,
            // 字符编码默认为0 utf-8
            CHARACTERENCODING: this.copyFromStyle ? this.copyFromShare.CHARACTERENCODING : '0',
            ENABLESHOWSNAPSHOT: this.copyFromStyle ? this.copyFromShare.ENABLESHOWSNAPSHOT : true,
        };
        // 更新模板创建的数据
        if (this.createAsShare) {
            defaultShare.DESCRIPTION = this.createAsShare.DESCRIPTION;
            defaultShare.CHARACTERENCODING = this.createAsShare.CHARACTERENCODING;
            defaultShare.ENABLESHOWSNAPSHOT = this.createAsShare.ENABLESHOWSNAPSHOT;
        }

        this.vm = new FormGroup({
            NAME: new FormControl(defaultShare.NAME, [this.NFSService.nfsShareNameValidator],
            ),
            FS: new FormControl(defaultShare.FS, [
                Validators.required,
            ]),
            dtree: new FormControl(defaultShare.dtree, [
            ]),
            DESCRIPTION: new FormControl(this.utilService.decodeHTML(defaultShare.DESCRIPTION), [
                DMValidators.rangeByteLength(0, 255),
            ]),
            CHARACTERENCODING: new FormControl(String(defaultShare.CHARACTERENCODING)),
            ENABLESHOWSNAPSHOT: new FormControl(defaultShare.ENABLESHOWSNAPSHOT, []),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            NAME: {
                nfsShareName: this.i18n.error.shareName,
            },
            FS: {
                required: this.toolService.requiredErr(this.i18n.label.fileSystem),
            },
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    collectionClients(): { share: NFSShare; permissions: NFSClient[] } {
        const vmValue = this.vmValue;
        const share: NFSShare = {
            NAME: vmValue.NAME,
            DESCRIPTION: vmValue.DESCRIPTION,
            CHARACTERENCODING: this.vm.value.CHARACTERENCODING,
            ENABLESHOWSNAPSHOT: this.vm.value.ENABLESHOWSNAPSHOT,
            SHAREPATH: this.share_path,
        };
        return { share, permissions: this.clientList };
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];
        // 如果是从copy from过来的，就不下发rest接口，只return数据回去
        if (this.copyFromStyle) {
            this.closeArgs.data = this.collectionClients();
        } else {
            if (this.clientList.length) {
                // 走omtask
                cmds.push(
                    this.buildNFSCmdByOmtask(),
                );
            } else {
                cmds.push(
                    this.buildNFSCmd(),
                );
            }
        }
        if (cmds.length > 1) {
            this.showAdvance = false;
        }
        return cmds;
    }

    /**
     * 选择文件系统后，需要更新dtree和sharepath
     */
    onChooseFileSystem($event): void {
        if (!$event.value) {
            this.share_path = '';
            return;
        }
        this.share_path = $event.value !== GlobalNamespaceE.ID ? `/${$event.option.data.NAME}/` : $event.option.data.NAME;
        this.isRootDirectory = $event.value === GlobalNamespaceE.ID;
        // 若选择根目录fs进行创建，则需要清空客户端权限列表,不可手动设置shareName
        if (this.isRootDirectory) {
            this.clientList = [];
            this.vm.controls.NAME.disable();
            this.share_name = '/';
        } else {
            this.vm.controls.NAME.enable();
        }
        this.selectedFilesystem = $event.option.data;

        if (this.hasDtreePermission) {
            this.dtreeRest = this.NFSService.getBatchDtreeRest(this.selectedFilesystem);
        }

        // 清空dtree
        this.vm.get('dtree').setValue(null);
        // 审计日志文件系统、哈希文件系统和内部文件系统要限制共享权限,同时清空已选客户端，如果本身就是基于审计日志文件系统的共享，不清空
        if ((!this.baseOnAuditLogFs && this.selectedFilesystem.isAuditLogFs) ||
            this.selectedFilesystem.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
            this.selectedFilesystem.SUBTYPE === FileSystemModeE.HASH) {
            this.entry = FileSystemEntryE.AuditLog;
            if (this.clientList.length) {
                // 清空权限给个提示
                if (this.selectedFilesystem.isAuditLogFs) {
                    this.toastService.warning(this.i18n.tip.permissionClearTip);
                }
                this.clientList = [];
            }
        } else {
            // 如果本身就是审计日志文件系统创建的共享再模板创建共享入口&选择是审计日志文件系统，entry设置为审计日志入口
            if (this.baseOnAuditLogFs && this.selectedFilesystem.isAuditLogFs) {
                this.entry = FileSystemEntryE.AuditLog;
            } else {
                this.entry = undefined;
            }
        }
    }

    /**
     * 选择dtree之后，需要更新sharepath
     */
    onChooseDtree($event): void {
        if (!$event.value) {
            this.share_path = `/${this.selectedFilesystem.NAME}/`;
            return;
        }
        // dtree的全路径是单个查的，这个时候要展示微观忙等
        this.pathLoading = true;
        this.NFSService.getDtree($event.option.data.ID, this.selectedFilesystem.vstoreId).pipe(
            finalize(() => {
                this.pathLoading = false;
            }),
        ).subscribe(({ data }: { data: Dtree }) => {
            this.share_path = this.dtreeService.getDtreeFullPath(this.selectedFilesystem, data);
        });
    }

    /**
     * 获取单个文件系统详情，判断是否是审计日志文件系统
     */
    getAuditLogFsInfo(id: string): void {
        this.NFSService.getFileSystem(id, this.vstoreId).subscribe((data: FileSystem) => {
            this.baseOnAuditLogFs = data.isAuditLogFs;
        });
    }

    /**
     * 创建文件系统
     */
    createFs(): void {
        this.createFsDisabled = true;

        let targetComponent: Type<ActionAbstract>;
        targetComponent = this.smartguiService.switch ? FsSmartGuiInfoComponent : CreateFileSystemComponent;
        this.doAction(targetComponent,
            {
                vstore: { label: this.vstoreName, value: this.vstoreId },
                onClose: ({ data }) => {
                    this.createFsDisabled = false;
                    if (!data) {
                        return;
                    }

                    if (data.NAME) {
                        this.addFsOptAndSelect(data);
                    } else if (data.taskId) {
                        this.initCreateFsTask(data);
                    }
                },
            },
        );
    }

    initCreateFsTask(data): void {
        this.createFsTaskId = data.taskId;
        if (this.createFs$) {
            this.createFs$.unsubscribe();
        }
        this.createFs$ = this.taskService.subscribe('fileSystem', (task, { id }: Omtask) => {
            if (id === this.createFsTaskId) {
                this.fsSelect.refresh();
            }
        });
    }

    /**
     * 文件系统下拉默认选中新建项
     *
     * @param {*} data 后台返回的原始对象
     *
     */
    addFsOptAndSelect(data: FileSystem): void {
        // 增加选项
        const newOption = {
            label: data.NAME,
            value: data.ID,
            data,
        };
        this.fsSelect.setFixedOption(newOption);
    }

    /**
     * 构建创建共享的rest
     */
    buildNFSCmd(): Command {
        const vmValue = this.vmValue;
        const params: NFSShare = {
            DESCRIPTION: vmValue.DESCRIPTION,
            SHAREPATH: this.share_path,
            CHARACTERENCODING: !this.isRootDirectory ? this.vm.value.CHARACTERENCODING : CharEncodingE.UTF8,
            ENABLESHOWSNAPSHOT: !this.isRootDirectory ? this.vm.value.ENABLESHOWSNAPSHOT : true,
        };

        if (ProductConfig.isHideNfsSnapShot) {
            delete params.ENABLESHOWSNAPSHOT;
        }

        if (this.selectedFilesystem && this.selectedFilesystem.vstoreId) {
            params.vstoreId = this.selectedFilesystem.vstoreId;
        }

        // 文件系统不选根目录并且指定了共享名称才下发
        if (!this.isRootDirectory && this.vm?.value?.NAME) {
            params.NAME = this.vm?.value?.NAME;
        }

        return {
            id: 'nfs_share',
            name: this.i18n.rest.createNFS,
            nameParams: [this.share_path],
            action: this.NFSService.create(params),
        };
    }

    /**
     * 构建走omtask创建共享的rest
     */
    buildNFSCmdByOmtask(): Command {
        const vmValue = this.vmValue;
        const params: CreateNfsParams = {
            DESCRIPTION: vmValue.DESCRIPTION,
            SHAREPATH: this.share_path,
            CHARACTERENCODING: this.vm.value.CHARACTERENCODING,
            ENABLESHOWSNAPSHOT: this.vm.value.ENABLESHOWSNAPSHOT,
        };

        if (ProductConfig.isHideNfsSnapShot) {
            delete params.ENABLESHOWSNAPSHOT;
        }

        if (this.selectedFilesystem && this.selectedFilesystem.vstoreId) {
            params.vstoreId = this.selectedFilesystem.vstoreId;
        }
        params.auth = this.buildNFSClientParams();

        // 文件系统不选根目录并且指定了共享名称才下发
        if (!this.isRootDirectory &&
            this.vm?.value?.NAME) {
            params.NAME = this.vm?.value?.NAME;
        }

        return {
            name: this.i18n.rest.createNFS,
            nameParams: [this.share_path],
            action: this.NFSService.createByOmtask(this.share_path, params),
        };
    }

    /**
     * 构建添加客户端下发参数
     */
    buildNFSClientParams(): NFSClient[] {
        return this.clientList.map((data) => ({
            ACCESSVAL: data.ACCESSVAL,
            ACCESSKRB5: data.ACCESSKRB5,
            ACCESSKRB5I: data.ACCESSKRB5I,
            ACCESSKRB5P: data.ACCESSKRB5P,
            ALLSQUASH: data.ALLSQUASH,
            NAME: data.NAME,
            ROOTSQUASH: data.ROOTSQUASH,
            SECURE: data.SECURE,
            ANONYMOUSID: data.ANONYMOUSID,
        }));
    }

    /**
     * 构建添加客户端的rest
     */
    buildNFSClientCmd(): Command {
        const params: AddNfsClientParams = {};

        params.auth = this.clientList.map((data) => ({
            ACCESSVAL: data.ACCESSVAL,
            ACCESSKRB5: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5,
            ACCESSKRB5I: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5I,
            ACCESSKRB5P: ProductConfig.isHideKerberos ? undefined : data.ACCESSKRB5P,
            ALLSQUASH: data.ALLSQUASH,
            NAME: data.NAME,
            ROOTSQUASH: data.ROOTSQUASH,
            SECURE: data.SECURE,
        }));

        // 没有客户端不创建任务
        if (!params.auth.length) {
            return null;
        }

        return {
            name: this.i18n.title.addNfsPermission,
            deps: ['nfs_share'],
            beforeAction: (shareRes): Observable<JobResponse> => {
                const { data } = shareRes;
                if (data && data.ID) {
                    params.nfsID = data.ID;
                    return Observable.create((observer) => {
                        observer.next();
                        observer.complete();
                    });
                } else {
                    return Observable.create((observer) => {
                        observer.error({
                            result: {
                                code: -1,
                            },
                        });
                    });
                }
            },
            action: (shareRes): Observable<JobResponse> => {
                const share: NFSShare = shareRes.data;
                params.vstoreId = share.vstoreId;
                return this.clientService.addClients(share.NAME, params, {
                    showResult: false,
                    frontendDisplayResult: false,
                });
            },
        };
    }

    /**
     * 复写exec，如果走omtask，就不走cmd的流程。
     */
    exec(): void {
        // 不走omtask
        if (!this.clientList.length) {
            super.exec();
            return;
        }

        // 走omtask
        const cmds = this.buildCmds();
        if (cmds && cmds.length === 1) {
            this.loading = true;
            (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                this.loading = false;
            })).subscribe(() => {
                this.closeArgs.refresh = true;
                this.hide();
            });
        } else {
            this.hide();
        }
    }

    /**
     * 复写ok函数，当pathloading的时候，不让点
     */
    onOk(): void {
        if (this.pathLoading) {
            return;
        }
        super.onOk();
    }
}
